/**
 * useConcurrentEditing.js
 * 
 * React hook that manages the Socket.io connection for concurrent editing.
 * Provides: presence list, field locks, lock/unlock functions, and
 * real-time field update broadcasting.
 * 
 * Drop into: /root/legalflow/frontend/src/hooks/useConcurrentEditing.js
 * 
 * Usage in RecordDetail.js:
 *   const {
 *     presenceUsers,
 *     fieldLocks,
 *     lockField,
 *     unlockField,
 *     broadcastFieldUpdate,
 *     incomingChanges,
 *     clearIncomingChange,
 *     conflict,
 *     resolveConflict,
 *     isConnected,
 *   } = useConcurrentEditing(recordId);
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

const HEARTBEAT_INTERVAL = 15000; // 15 seconds, matches backend

export default function useConcurrentEditing(recordId) {
  const { token, user } = useAuthStore();
  const socketRef = useRef(null);
  const heartbeatRef = useRef(null);
  const lockRefreshTimers = useRef({});

  const [isConnected, setIsConnected] = useState(false);
  const [presenceUsers, setPresenceUsers] = useState([]);
  const [fieldLocks, setFieldLocks] = useState({});       // { fieldName: lockObj }
  const [incomingChanges, setIncomingChanges] = useState({}); // { fieldName: { value, changedBy, savedAt } }
  const [conflict, setConflict] = useState(null);          // conflict object from 409 response

  // ─── Connect socket when recordId changes ───────────────
  useEffect(() => {
    if (!recordId || !token) return;

    // Connect to the same host on port 4001 (API port)
    const apiHost = `${window.location.protocol}//${window.location.hostname}`;
    const socket = io(apiHost, {
      auth: { token, userId: user?.id, firstName: user?.first_name, lastName: user?.last_name, email: user?.email },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      socket.emit('join_record', { recordId });
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
      setPresenceUsers([]);
      setFieldLocks({});
    });

    socket.on('reconnect', () => {
      socket.emit('join_record', { recordId });
    });

    // ── Presence events ───────────────────────────────────
    socket.on('presence_state', ({ users, locks }) => {
      setPresenceUsers(users.filter(u => u.userId !== user?.id));
      setFieldLocks(locks || {});
    });

    socket.on('user_joined', ({ user: joinedUser, users }) => {
      setPresenceUsers(users.filter(u => u.userId !== user?.id));
      if (joinedUser.userId !== user?.id) {
        toast(`${joinedUser.firstName} opened this record`, {
          icon: '👤',
          duration: 3000,
          style: { background: '#1F2937', color: '#F9FAFB', fontSize: '13px' },
        });
      }
    });

    socket.on('user_left', ({ userId: leftUserId, users }) => {
      setPresenceUsers(users.filter(u => u.userId !== user?.id));
      // Clean up any locks held by the user who left
      setFieldLocks(prev => {
        const next = { ...prev };
        for (const [fieldName, lock] of Object.entries(next)) {
          if (lock.userId === leftUserId) {
            delete next[fieldName];
          }
        }
        return next;
      });
    });

    // ── Field lock events ─────────────────────────────────
    socket.on('field_locked', ({ fieldName, lock }) => {
      setFieldLocks(prev => ({ ...prev, [fieldName]: lock }));
    });

    socket.on('field_unlocked', ({ fieldName }) => {
      setFieldLocks(prev => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    });

    socket.on('lock_denied', ({ fieldName, lock, reason }) => {
      toast.error(reason || `Field locked by ${lock?.firstName}`, {
        duration: 3000,
        style: { fontSize: '13px' },
      });
    });

    // ── Real-time field changes from other users ──────────
    socket.on('field_changed', ({ fieldName, value, savedAt, changedBy }) => {
      setIncomingChanges(prev => ({
        ...prev,
        [fieldName]: { value, changedBy, savedAt, receivedAt: Date.now() },
      }));
    });

    // ── Full record update (another user saved) ───────────
    socket.on('record_updated', ({ fieldData, savedAt, savedBy }) => {
      toast(`${savedBy.firstName} saved changes`, {
        icon: '💾',
        duration: 4000,
        style: { background: '#1F2937', color: '#F9FAFB', fontSize: '13px' },
      });
      // Apply all field changes at once
      if (fieldData) {
        const changes = {};
        for (const [fieldName, value] of Object.entries(fieldData)) {
          changes[fieldName] = { value, changedBy: savedBy, savedAt, receivedAt: Date.now() };
        }
        setIncomingChanges(prev => ({ ...prev, ...changes }));
      }
    });

    // ── Error handler ─────────────────────────────────────
    socket.on('ws_error', ({ event, message }) => {
      console.error(`[WS] Error in ${event}:`, message);
    });

    // ── Heartbeat ─────────────────────────────────────────
    heartbeatRef.current = setInterval(() => {
      if (socket.connected) {
        socket.emit('heartbeat', { recordId });
      }
    }, HEARTBEAT_INTERVAL);

    // ── Cleanup on unmount or recordId change ─────────────
    return () => {
      clearInterval(heartbeatRef.current);
      // Clear all lock refresh timers
      Object.values(lockRefreshTimers.current).forEach(clearInterval);
      lockRefreshTimers.current = {};

      if (socket.connected) {
        socket.emit('leave_record', { recordId });
      }
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      setPresenceUsers([]);
      setFieldLocks({});
      setIncomingChanges({});
    };
  }, [recordId, token, user?.id]);

  // ─── Lock a field (call on focus) ─────────────────────────
  const lockField = useCallback((fieldName) => {
    if (!socketRef.current?.connected || !recordId) return;
    socketRef.current.emit('lock_field', { recordId, fieldName });

    // Start auto-refresh to keep the lock alive while editing
    if (lockRefreshTimers.current[fieldName]) {
      clearInterval(lockRefreshTimers.current[fieldName]);
    }
    lockRefreshTimers.current[fieldName] = setInterval(() => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('refresh_lock', { recordId, fieldName });
      }
    }, 10000); // Refresh every 10s (lock TTL is 30s)
  }, [recordId]);

  // ─── Unlock a field (call on blur) ────────────────────────
  const unlockField = useCallback((fieldName) => {
    if (!socketRef.current?.connected || !recordId) return;
    socketRef.current.emit('unlock_field', { recordId, fieldName });

    if (lockRefreshTimers.current[fieldName]) {
      clearInterval(lockRefreshTimers.current[fieldName]);
      delete lockRefreshTimers.current[fieldName];
    }
  }, [recordId]);

  // ─── Broadcast a field change to other users ──────────────
  const broadcastFieldUpdate = useCallback((fieldName, value) => {
    if (!socketRef.current?.connected || !recordId) return;
    socketRef.current.emit('field_update', {
      recordId,
      fieldName,
      value,
      savedAt: Date.now(),
    });
  }, [recordId]);

  // ─── Broadcast a full record save ─────────────────────────
  const broadcastRecordSaved = useCallback((fieldData) => {
    if (!socketRef.current?.connected || !recordId) return;
    socketRef.current.emit('record_saved', {
      recordId,
      fieldData,
      savedAt: Date.now(),
    });
  }, [recordId]);

  // ─── Clear an incoming change (after applying it) ─────────
  const clearIncomingChange = useCallback((fieldName) => {
    setIncomingChanges(prev => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  // ─── Handle conflict from API 409 ─────────────────────────
  const setConflictFromResponse = useCallback((conflictData) => {
    setConflict(conflictData);
  }, []);

  const resolveConflict = useCallback((resolution) => {
    // resolution: 'keep_mine' | 'keep_theirs' | 'merge'
    // The actual resolution logic is handled by the calling component
    setConflict(null);
    return resolution;
  }, []);

  // ─── Check if current user holds a lock ────────────────────
  const isLockedByMe = useCallback((fieldName) => {
    const lock = fieldLocks[fieldName];
    return lock?.userId === user?.id;
  }, [fieldLocks, user?.id]);

  // ─── Check if another user holds a lock ────────────────────
  const isLockedByOther = useCallback((fieldName) => {
    const lock = fieldLocks[fieldName];
    return lock && lock.userId !== user?.id ? lock : null;
  }, [fieldLocks, user?.id]);

  return {
    // State
    isConnected,
    presenceUsers,
    fieldLocks,
    incomingChanges,
    conflict,

    // Actions
    lockField,
    unlockField,
    broadcastFieldUpdate,
    broadcastRecordSaved,
    clearIncomingChange,
    setConflictFromResponse,
    resolveConflict,

    // Helpers
    isLockedByMe,
    isLockedByOther,
  };
}
